import React, { useState, useEffect } from "react";
import API from "../../api/axios";
import useAdminScope from "../../hooks/useAdminScope";
import {
  KeyRound,
  Loader2,
  Printer,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  X,
} from "lucide-react";

// Issuing the logins students use for the app. Only preparatory and
// secondary grades appear here — younger years are followed by their
// parents, who keep their own account either way.
//
// The one thing this screen must get right: a password exists in readable
// form ONLY in the response that created it (they are stored hashed). So
// whatever comes back is held on screen until the admin has printed it,
// and the screen says so plainly rather than letting them navigate away
// and discover it later.
const GENDER_SECTIONS = [
  { key: "male", label: "البنين" },
  { key: "female", label: "البنات" },
  { key: "", label: "غير محدد" },
];

const byClassThenName = (a, b) =>
  (a.classroom || "").localeCompare(b.classroom || "", "ar") ||
  (a.fullName || "").localeCompare(b.fullName || "", "ar");

// The printed sheet, split by gender; empty sections are left out.
const sectionsOf = (rows) =>
  GENDER_SECTIONS.map((section) => ({
    ...section,
    rows: rows
      .filter((row) =>
        section.key ? row.gender === section.key : !["male", "female"].includes(row.gender),
      )
      .sort(byClassThenName),
  })).filter((section) => section.rows.length > 0);

// A school's own admin opens this as a page of their panel. The platform
// owner opens it from the schools page as a window over one `school` — they
// have no school of their own, so every request names it (?school=id).
const StudentAccountManager = ({ school = null, onClose } = {}) => {
  const { canEdit } = useAdminScope();
  const scope = school ? { school: school._id } : undefined;
  const [grades, setGrades] = useState([]);
  const [grade, setGrade] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [issued, setIssued] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    API.get("/student-accounts/grades", { params: scope })
      .then((res) => {
        const list = res.data?.data || [];
        setGrades(list);
        if (list.length === 1) setGrade(list[0]._id);
      })
      .catch(() => showToast("تعذّر تحميل المراحل", "error"));
  }, []);

  const loadStudents = async (gradeId) => {
    if (!gradeId) return setStudents([]);
    setLoading(true);
    try {
      const res = await API.get("/student-accounts", { params: { grade: gradeId, ...scope } });
      setStudents(res.data?.data || []);
    } catch (err) {
      showToast(err.response?.data?.message || "تعذّر تحميل الطلاب", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents(grade);
  }, [grade]);

  const withoutAccount = students.filter((s) => !s.hasAccount);

  const issueAll = async () => {
    setWorking(true);
    try {
      const res = await API.post("/student-accounts/issue", { grade }, { params: scope });
      setIssued({
        title: "بيانات دخول الطلاب",
        gradeName: grades.find((g) => g._id === grade)?.name || "",
        rows: res.data?.data || [],
      });
      showToast(res.data?.message || "تم الإصدار");
      loadStudents(grade);
    } catch (err) {
      showToast(err.response?.data?.message || "تعذّر إصدار الحسابات", "error");
    } finally {
      setWorking(false);
    }
  };

  const withAccount = students.filter((s) => s.hasAccount);

  const reissueGrade = async () => {
    const gradeName = grades.find((g) => g._id === grade)?.name || "";
    const ok = window.confirm(
      `هيتعمل كلمات مرور جديدة لـ ${withAccount.length} طالب في ${gradeName}.\n` +
        "الأكواد هتفضل زي ما هي، بس كلمات المرور القديمة هتبطل فورًا — أي كشف مطبوع قبل كده مش هيشتغل.\n\nكمّل؟",
    );
    if (!ok) return;

    setWorking(true);
    try {
      const res = await API.post("/student-accounts/reissue", { grade }, { params: scope });
      setIssued({
        title: "كلمات مرور جديدة",
        gradeName,
        rows: res.data?.data || [],
      });
      showToast(res.data?.message || "تم الإصدار");
    } catch (err) {
      showToast(err.response?.data?.message || "تعذّرت إعادة الإصدار", "error");
    } finally {
      setWorking(false);
    }
  };

  const reissue = async (student) => {
    setWorking(true);
    try {
      const res = await API.post(`/student-accounts/${student._id}/reissue`, {}, { params: scope });
      setIssued({
        title: "كلمة مرور جديدة",
        gradeName: grades.find((g) => g._id === grade)?.name || "",
        rows: [{ ...res.data.data, classroom: student.classroom }],
      });
      showToast(res.data.message);
    } catch (err) {
      showToast(err.response?.data?.message || "تعذّرت إعادة الإصدار", "error");
    } finally {
      setWorking(false);
    }
  };

  // Which section alone goes to paper ("male" / "female"), or null for both.
  const [printOnly, setPrintOnly] = useState(null);

  useEffect(() => {
    const reset = () => setPrintOnly(null);
    window.addEventListener("afterprint", reset);
    return () => window.removeEventListener("afterprint", reset);
  }, []);

  const print = () => {
    setPrintOnly(null);
    setTimeout(() => window.print(), 50);
  };

  const printSection = (key) => {
    setPrintOnly(key);
    setTimeout(() => window.print(), 50);
  };

  const page = (
    <div className={school ? "p-6" : "p-8 bg-[#F8FAFC] min-h-screen"} dir="rtl">
      {toast && (
        <div
          className={`fixed bottom-6 left-6 z-[60] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-xl border text-sm font-bold no-print ${
            toast.type === "success"
              ? "bg-emerald-50 border-emerald-100 text-emerald-800"
              : "bg-rose-50 border-rose-100 text-rose-800"
          }`}
        >
          {toast.type === "success" ? (
            <CheckCircle2 className="text-emerald-500" size={20} />
          ) : (
            <XCircle className="text-rose-500" size={20} />
          )}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="mr-2 p-1 hover:bg-black/5 rounded-lg">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {!school && (
          <div className="flex items-center gap-4 mb-8 no-print">
            <div className="bg-indigo-600 p-3 rounded-2xl text-white shadow-lg shadow-indigo-100">
              <KeyRound size={28} />
            </div>
            <div>
              <h1 className="text-3xl font-black text-slate-800 tracking-tight">
                بيانات دخول الطلاب
              </h1>
              <p className="text-slate-400 font-medium text-sm italic">
                للمرحلة الإعدادية والثانوية — كود دخول لكل طالب مع كلمة مرور ثابتة
              </p>
            </div>
          </div>
        )}

        {/* The printable sheet. Kept mounted above everything else while it
            exists, because these passwords cannot be shown a second time. */}
        {issued && (
          <div className="bg-white rounded-[2rem] border-2 border-emerald-200 shadow-sm mb-8 overflow-hidden print-area">
            <div className="p-6 bg-emerald-50/60 border-b border-emerald-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 no-print">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-500 shrink-0" size={20} />
                <div>
                  <p className="font-black text-slate-800 text-sm">
                    اطبع الكشف ده دلوقتي
                  </p>
                  <p className="text-[12px] font-bold text-slate-500 mt-0.5">
                    كلمات المرور متخزنة مشفّرة — مش هتقدر تشوفها تاني بعد ما تقفل
                    الصفحة. لو ضاعت، هتحتاج تعمل إعادة إصدار.
                  </p>
                  <p className="text-[12px] font-bold text-slate-500 mt-0.5">
                    من نافذة الطباعة اختار «حفظ بصيغة PDF» عشان تحتفظ بنسخة عندك
                    قبل ما تطبع على ورق.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={print}
                  className="px-5 py-3 bg-slate-900 text-white rounded-xl font-black text-xs flex items-center gap-2 hover:bg-indigo-600 transition-colors"
                >
                  <Printer size={16} /> طباعة الكل
                </button>
                <button
                  onClick={() => setIssued(null)}
                  className="px-5 py-3 bg-slate-100 text-slate-600 rounded-xl font-black text-xs hover:bg-slate-200 transition-colors"
                >
                  تم، اقفل
                </button>
              </div>
            </div>

            {/* Boys and girls on separate sheets, each sorted by class then
                name, so each list can be handed out on its own. */}
            {sectionsOf(issued.rows).map((section, index) => (
              <div
                key={section.key}
                className={`p-6 ${index > 0 ? "border-t-4 border-slate-100" : ""} ${
                  printOnly && printOnly !== section.key ? "print-skip" : ""
                } ${!printOnly && index > 0 ? "print-page-break" : ""}`}
              >
                <div className="flex items-center justify-between mb-3 no-print">
                  <h3 className="font-black text-slate-700">
                    {section.label}{" "}
                    <span className="text-slate-400 text-sm">({section.rows.length})</span>
                  </h3>
                  <button
                    onClick={() => printSection(section.key)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl font-black text-xs flex items-center gap-2 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                  >
                    <Printer size={14} /> طباعة {section.label} بس
                  </button>
                </div>
                {/* Only on paper: which grade this sheet belongs to and when
                    it was issued, so a printout found later is identifiable
                    on its own. */}
                <div className="hidden print:block mb-4">
                  <h2 className="text-xl font-black">
                    {issued.title} — {issued.gradeName} — {section.label}
                  </h2>
                  <p className="text-sm font-bold">
                    {new Date().toLocaleDateString("ar-EG")} · العدد: {section.rows.length}
                  </p>
                </div>
                <table className="w-full text-right">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="p-3 text-[11px] font-black text-slate-400 uppercase">م</th>
                      <th className="p-3 text-[11px] font-black text-slate-400 uppercase">الطالب</th>
                      <th className="p-3 text-[11px] font-black text-slate-400 uppercase">الفصل</th>
                      <th className="p-3 text-[11px] font-black text-slate-400 uppercase">كود الدخول</th>
                      <th className="p-3 text-[11px] font-black text-slate-400 uppercase">كلمة المرور</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {section.rows.map((row, i) => (
                      <tr key={row.student}>
                        <td className="p-3 font-bold text-slate-400 text-xs">{i + 1}</td>
                        <td className="p-3 font-bold text-slate-700 text-sm">{row.fullName}</td>
                        <td className="p-3 font-bold text-slate-400 text-xs">{row.classroom || "—"}</td>
                        <td className="p-3 font-mono font-black text-slate-800 tracking-widest">
                          {row.username}
                        </td>
                        <td className="p-3 font-mono font-black text-indigo-700 tracking-widest">
                          {row.password}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-4 mb-8 no-print">
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="bg-white border border-slate-200 px-6 py-4 rounded-2xl font-bold text-slate-600 outline-none focus:border-indigo-500 shadow-sm cursor-pointer flex-grow"
          >
            <option value="">اختر المرحلة</option>
            {grades.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </select>

          {canEdit && grade && withoutAccount.length > 0 && (
            <button
              onClick={issueAll}
              disabled={working}
              className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200 disabled:opacity-60"
            >
              {working ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
              إصدار لـ {withoutAccount.length} طالب
            </button>
          )}

          {canEdit && grade && withAccount.length > 0 && (
            <button
              onClick={reissueGrade}
              disabled={working}
              className="bg-white text-slate-700 border-2 border-slate-200 px-6 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:border-indigo-400 hover:text-indigo-700 transition-all disabled:opacity-60"
            >
              {working ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
              كلمات مرور جديدة لـ {withAccount.length} طالب
            </button>
          )}
        </div>

        {grades.length === 0 && (
          <div className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center no-print">
            <p className="text-slate-400 font-bold">
              مفيش مراحل إعدادية أو ثانوية في المدرسة دي — حسابات الطلاب متاحة
              للمرحلتين دول بس.
            </p>
          </div>
        )}

        {grade && (
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden no-print">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <p className="font-black text-slate-700 text-sm">
                {students.length} طالب — {students.filter((s) => s.hasAccount).length} لهم
                حسابات
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-right">
                <thead>
                  <tr className="bg-slate-50/50 border-b border-slate-100">
                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-widest">الطالب</th>
                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-widest">الفصل</th>
                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-widest">كود الدخول</th>
                    <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-widest text-center">إجراء</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="p-16 text-center">
                        <Loader2 className="animate-spin inline text-indigo-600" size={36} />
                      </td>
                    </tr>
                  ) : students.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-12 text-center text-slate-400 font-bold">
                        مفيش طلاب في المرحلة دي.
                      </td>
                    </tr>
                  ) : (
                    students.map((student) => (
                      <tr key={student._id} className="hover:bg-indigo-50/30 transition-colors">
                        <td className="p-5 font-black text-slate-700 text-sm">{student.fullName}</td>
                        <td className="p-5 font-bold text-slate-400 text-xs">
                          {student.classroom || "بدون فصل"}
                        </td>
                        <td className="p-5">
                          {student.username ? (
                            <span className="font-mono font-black text-slate-800 tracking-widest">
                              {student.username}
                            </span>
                          ) : (
                            <span className="text-[11px] font-black text-slate-300">
                              لسه مفيش حساب
                            </span>
                          )}
                        </td>
                        <td className="p-5 text-center">
                          {canEdit && student.hasAccount && (
                            <button
                              onClick={() => reissue(student)}
                              disabled={working}
                              title="كلمة مرور جديدة"
                              className="p-3 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all disabled:opacity-50"
                            >
                              <RefreshCw size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        /* The sheet is inside the admin dashboard's own layout (sidebar,
           header, the students table below it), so hiding this screen's
           own chrome isn't enough — everything on the page is hidden and
           only the sheet is put back, at the top of the paper. */
        @media print {
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area {
            position: absolute; inset: 0 auto auto 0; width: 100%;
            border: none !important; box-shadow: none !important; margin: 0;
          }
          .no-print { display: none !important; }
          .print-skip { display: none !important; }
          /* Opened as a window from the schools page: the window must not
             clip or pin the sheet to one screen's height on paper. */
          .sam-overlay, .sam-panel {
            position: static !important; display: block !important;
            overflow: visible !important; max-height: none !important;
            padding: 0 !important; background: white !important;
            box-shadow: none !important; backdrop-filter: none !important;
          }
          .print-page-break { break-before: page; page-break-before: always; border: none !important; }
          body { background: white; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
          thead { display: table-header-group; }
        }
      `,
        }}
      />
    </div>
  );

  if (!school) return page;

  return (
    <div className="sam-overlay fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="sam-panel bg-[#F8FAFC] w-full max-w-6xl rounded-[2.5rem] shadow-2xl overflow-y-auto max-h-[92vh]">
        <div className="p-6 bg-indigo-600 text-white flex justify-between items-center no-print" dir="rtl">
          <div>
            <h2 className="text-lg font-black">بيانات دخول الطلاب — {school.name}</h2>
            <p className="text-indigo-50 text-xs font-bold mt-1">
              أكواد دخول تطبيق الطلاب للمرحلة الإعدادية والثانوية، مع كلمة مرور لكل طالب.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X />
          </button>
        </div>
        {page}
      </div>
    </div>
  );
};

export default StudentAccountManager;
